import React, { useRef, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Bookmark, ChevronDown, ChevronUp } from "lucide-react";
import { SIZES } from "../data/products";
import { useStore, formatPrice } from "../context/StoreContext";
import ProductCard from "../components/product/ProductCard";
import AutoplayVideo from "../components/AutoplayVideo";

const Accordion = ({ title, children, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border-b border-ink/12">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between py-4 font-mono text-xs uppercase tracking-widest"
        aria-expanded={open}
      >
        <span>{title}</span>
        {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
      </button>
      {open && (
        <div className="pb-5 font-body text-base md:text-lg text-ink/85 leading-relaxed">
          {children}
        </div>
      )}
    </div>
  );
};

const ProductDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToCart, currency, toggleWishlist, wishlist, products, productsLoading, findProductById } = useStore();
  const product = findProductById(id);
  const [size, setSize] = useState(null);
  const [activeIdx, setActiveIdx] = useState(0);
  const scrollerRef = useRef(null);

  if (productsLoading && !product) {
    return (
      <div className="pt-[140px] text-center min-h-[60vh]" data-testid="page-product-loading">
        <p className="font-mono text-xs uppercase tracking-widest text-ink/60">LOADING...</p>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="pt-[140px] text-center min-h-[60vh]" data-testid="page-product-notfound">
        <h1 className="font-display uppercase text-5xl">NOT FOUND</h1>
        <Link to="/shop" className="inline-block mt-6 btn-ghost">Back to shop</Link>
      </div>
    );
  }

  const soldOut = product.stockQty === 0;
  const saved = wishlist.includes(product.id);
  const videos = product.videos || [];
  const mediaItems = [
    ...product.images.map((src) => ({ type: "image", src })),
    ...videos.map((v) => ({ type: "video", src: v.src, poster: v.poster })),
  ];
  const related = products.filter(
    (p) => p.id !== product.id && p.category === product.category
  ).slice(0, 4);
  const more = products.filter((p) => p.id !== product.id).slice(0, 8);

  const handleAdd = (thenCheckout = false) => {
    if (soldOut) {
      toast("SOLD OUT");
      return;
    }
    if (!size) {
      toast("PICK A SIZE FIRST");
      return;
    }
    addToCart(product, size, 1);
    toast(`${product.name.toUpperCase()} / SIZE ${size} ADDED`);
    if (thenCheckout) navigate("/checkout");
  };

  const onScroll = () => {
    const el = scrollerRef.current;
    if (!el) return;
    const w = el.clientWidth;
    setActiveIdx(Math.round(el.scrollLeft / w));
  };

  return (
    <div data-testid="page-product" className="pt-[80px]">
      <div className="max-w-[1440px] mx-auto px-4 md:px-12 py-8 md:py-12">
        <p className="font-mono text-[11px] uppercase tracking-widest text-ink/60 mb-6">
          HOME / SHOP / {product.category}S / {product.id.toUpperCase()}
        </p>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16">
          {/* Gallery */}
          <div>
            {/* Mobile / single column carousel */}
            <div className="lg:hidden">
              <div
                ref={scrollerRef}
                onScroll={onScroll}
                className="flex overflow-x-auto snap-row hide-scrollbar rounded-2xl shadow-card"
                style={{ aspectRatio: "3 / 4" }}
              >
                {mediaItems.map((m, i) =>
                  m.type === "image" ? (
                    <img
                      key={m.src}
                      src={m.src}
                      alt={`${product.name} ${i + 1}`}
                      className="w-full flex-shrink-0 object-cover h-full"
                      style={{ width: "100%" }}
                    />
                  ) : (
                    <AutoplayVideo
                      key={m.src}
                      src={m.src}
                      poster={m.poster}
                      className="w-full flex-shrink-0 object-cover h-full bg-charcoal"
                      style={{ width: "100%" }}
                    />
                  )
                )}
              </div>
              {mediaItems.length > 1 && (
                <div className="flex gap-2 mt-3 overflow-x-auto hide-scrollbar">
                  {mediaItems.map((m, i) => (
                    <button
                      key={m.src}
                      onClick={() => {
                        const el = scrollerRef.current;
                        if (el) el.scrollTo({ left: i * el.clientWidth, behavior: "smooth" });
                      }}
                      className={`relative w-16 h-20 flex-shrink-0 border ${
                        i === activeIdx ? "border-ink" : "border-ink/30"
                      }`}
                    >
                      <img
                        src={m.type === "image" ? m.src : m.poster}
                        alt=""
                        className="w-full h-full object-cover"
                      />
                      {m.type === "video" && (
                        <span className="absolute inset-0 flex items-center justify-center text-cream font-mono text-[10px] bg-ink/40">
                          VIDEO
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Desktop vertical stack */}
            <div className="hidden lg:flex flex-col gap-4">
              {product.images.map((src, i) => (
                <img
                  key={src}
                  src={src}
                  alt={`${product.name} ${i + 1}`}
                  className="w-full object-cover rounded-[20px] shadow-card"
                  style={{ aspectRatio: "3 / 4" }}
                  loading={i === 0 ? "eager" : "lazy"}
                />
              ))}
              {videos.map((v) => (
                <AutoplayVideo
                  key={v.src}
                  src={v.src}
                  poster={v.poster}
                  className="w-full object-cover rounded-[20px] shadow-card bg-charcoal"
                  style={{ aspectRatio: "3 / 4" }}
                />
              ))}
            </div>
          </div>

          {/* Info column */}
          <div className="lg:sticky lg:top-[100px] lg:self-start">
            <span
              className={`inline-block font-mono text-[10px] uppercase tracking-widest px-2 py-1 mb-4 ${
                product.category === "EXCLUSIVE"
                  ? "bg-dc-red text-cream"
                  : "bg-ink text-cream"
              }`}
            >
              {product.category}
            </span>
            <h1 className="font-display uppercase text-4xl sm:text-5xl md:text-6xl leading-none">
              {product.name}
            </h1>
            <p
              data-testid="product-price"
              className="font-mono text-base md:text-lg mt-4 text-ink"
            >
              {formatPrice(product.priceInr, currency)}
            </p>
            <p className="font-body text-lg md:text-xl mt-5 text-ink/85 leading-relaxed">
              {product.description}
            </p>

            <div className="mt-7">
              <p className="font-mono text-[11px] uppercase tracking-widest text-ink/70 mb-2">
                Pick a size
              </p>
              <div className="flex gap-2">
                {SIZES.map((s) => (
                  <button
                    key={s}
                    onClick={() => setSize(s)}
                    data-testid={`product-size-${s}`}
                    className={`w-12 h-12 rounded-full border border-ink/15 font-mono text-sm transition-colors ${
                      size === s
                        ? "bg-ink text-cream"
                        : "bg-transparent text-ink hover:bg-ink/10"
                    }`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-7">
              <button
                onClick={() => handleAdd(false)}
                data-testid="product-add-to-cart"
                disabled={soldOut}
                className="btn-ghost"
              >
                {soldOut ? "Sold out" : "Add to cart"}
              </button>
              <button
                onClick={() => handleAdd(true)}
                data-testid="product-buy-now"
                disabled={soldOut}
                className="btn-solid"
              >
                {soldOut ? "Sold out" : "Buy now"}
              </button>
            </div>

            <button
              onClick={() => toggleWishlist(product.id)}
              data-testid="product-wishlist"
              className="mt-4 inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest hover:text-dc-red transition-colors"
            >
              <Bookmark
                size={16}
                strokeWidth={1.5}
                className={saved ? "fill-dc-red text-dc-red" : ""}
              />
              {saved ? "Saved" : "Save to wishlist"}
            </button>

            <div className="mt-8">
              <Accordion title="Size chart">
                <p>S fits 28-30 / M 30-32 / L 32-34 / XL 34-36. Bottoms run slightly oversized. If between sizes, size down.</p>
              </Accordion>
              <Accordion title="Materials &amp; care">
                <p>{product.materials} Cold wash inside out. No tumble dry. Iron on reverse. Built to age.</p>
              </Accordion>
              <Accordion title="Shipping &amp; returns">
                <p>Free shipping within India on orders over &#8377;5000. International calculated at checkout. Staples eligible for 14-day return. Exclusives are final sale.</p>
              </Accordion>
            </div>
          </div>
        </div>

        {/* Match with */}
        {related.length > 0 && (
          <section className="mt-20">
            <h2 className="font-display uppercase text-3xl md:text-5xl mb-6">
              Match with
            </h2>
            <div className="flex gap-4 overflow-x-auto hide-scrollbar pb-2">
              {related.map((p) => (
                <div key={p.id} className="w-[240px] md:w-[280px] flex-shrink-0">
                  <ProductCard product={p} />
                </div>
              ))}
            </div>
          </section>
        )}

        {/* More from Damage Culture */}
        <section className="mt-20">
          <h2 className="font-display uppercase text-3xl md:text-5xl mb-6">
            More from Damage Culture
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-5">
            {more.map((p) => (
              <ProductCard key={p.id} product={p} />
            ))}
          </div>
        </section>
      </div>
    </div>
  );
};

export default ProductDetailPage;
